import { DialogContent, Divider } from '@mui/material'

import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { useGetEvent, useGetEventConfig } from 'api/events/events.api'
import DOMPurify from 'dompurify'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import alertStore from 'store/alert/alert'
import filterStore from 'store/filter/filter'
import { GROUPING_QUERYSTRING_PARAM } from 'store/filter/urlFilter.helper'
import { Button } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { Dialog } from 'ui/components/Dialog/Dialog'
import CopyDialogTitle from 'ui/molecules/CopyDialogTitle/CopyDialogTitle'
import PrintButton from 'ui/molecules/PrintButton/PrintButton'
import Datetime from 'utils/datetime/datetime'
import { getPropertiesFromDigitalTwinModel } from 'utils/digitalTwinSettings/digitalTwinSettingUtils'
import { snapshot, useSnapshot } from 'valtio'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import { sandboxProjectStore } from 'views/SandboxProjectView/store/SandboxProjectStore'
import EventPostModal from '../EventPostModal/EventPostModal'
import {
  EVENT_TYPES,
  getLabelForEventStatus,
  isTimeSeriesSettingInstruction,
  LinkedObjectSettingInstructions,
  SCHEDULABILITIES,
  SettingOrTimeSeriesInstructions,
  TimeSeriesSettingInstruction,
} from '../events.helper'
import { getEventDescriptionHtml } from './EventPostViewModal.helper'
import styles from './EventPostViewModal.module.less'

type EventPostViewModalProps = {
  closeModal: () => void
  selectedEventPostId?: number
}

const getSettingValueFromInstruction = (setting: SettingOrTimeSeriesInstructions): string => {
  if ('values' in setting && Array.isArray(setting.values)) {
    const values = (setting as TimeSeriesSettingInstruction).values.filter(
      (value): value is number => value !== null && value !== undefined
    )

    if (values.length === 0) return 'N/A'

    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)
    return `${minValue}-${maxValue}`
  }

  if ('value' in setting) {
    if (typeof setting.value === 'number') {
      return setting.value.toString()
    } else if (typeof setting.value === 'string') {
      return setting.value
    }
  }

  return 'N/A'
}

export default function EventPostViewModal({ closeModal, selectedEventPostId }: EventPostViewModalProps): ReactElement {
  const { t } = useTranslation()
  const location = useLocation()
  const { activeSystem, systemId } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)
  const { data: selectedEventPost } = useGetEvent(selectedEventPostId)
  const filterSnap = snapshot(filterStore)

  const eventConfig = useGetEventConfig(systemId).data
  const eventCategories = eventConfig?.categories || []

  const { isSandboxProject } = useSnapshot(sandboxProjectStore)

  const allProperties = useMemo(() => {
    return digitalTwin?.model ? getPropertiesFromDigitalTwinModel(digitalTwin.name, digitalTwin.model) : []
  }, [digitalTwin])

  const [openEventPostModal, setOpenEventPostModal] = useState(false)

  const handleOnClose = useCallback((): void => {
    closeModal()
  }, [closeModal])

  if (!selectedEventPost) {
    return <></>
  }

  const handleEditClick = (): void => {
    const urlParams = new URLSearchParams(window.location.search)

    // Group should only show in URL if events route has grouping
    if (!filterSnap.groupingPerRoute['events']) {
      urlParams.delete(GROUPING_QUERYSTRING_PARAM)
    }

    const url = new URL(window.location.origin)
    url.pathname = `/events/${selectedEventPostId}/edit`
    url.search = urlParams.toString()
    url.hash = window.location.hash
    const newWindow = window.open(url, '_blank')

    if (newWindow) {
      newWindow.onload = () => {
        ReactDOM.createPortal(
          <EventPostModal selectedEventPostId={selectedEventPostId} closeModal={() => setOpenEventPostModal(false)} />,
          newWindow.document.body
        )
      }
    }
  }

  const renderInformation = (): ReactElement => {
    return (
      <>
        <div className="flex">
          <div className="flex flex-col w-1/2 m-3">
            <div className="flex flex-col *:m-4 *:text-2xl">
              <div>
                <strong>{`${t('Name')}: `}</strong> {selectedEventPost.name}
              </div>
              <div>
                <strong>{`${t('Event type')}: `}</strong>
                {t(
                  EVENT_TYPES.find((eventType) => eventType.value === selectedEventPost.operational_event_type)
                    ?.label || '-'
                )}
              </div>
              <div>
                <strong>{`${t('Cause category')}: `}</strong>
                {selectedEventPost.category_id
                  ? eventCategories.find((eventType) => eventType.id === selectedEventPost.category_id)?.name
                  : ''}
              </div>
              <div>
                <strong>{`${t('Requires UMM')}: `}</strong> {selectedEventPost.require_umm ? t('Yes') : t('No')}
              </div>
              <div>
                <strong>{`${t('Cancelable after start')}: `}</strong>
                {selectedEventPost.cancelable_after_start ? t('Yes') : t('No')}
              </div>
            </div>

            <div className="flex flex-col *:m-4 *:text-2xl">
              <div>
                <strong>{`${t('Earliest start time')}: `}</strong>
                {selectedEventPost.earliest_start_time
                  ? Datetime.toLocalTime(selectedEventPost.earliest_start_time, 'longDay')
                  : '-'}
              </div>
              <div>
                <strong>{`${t('Latest end time')}: `}</strong>
                {selectedEventPost.latest_end_time
                  ? Datetime.toLocalTime(selectedEventPost.latest_end_time, 'longDay')
                  : '-'}
              </div>
            </div>
          </div>

          <div className="flex flex-col w-1/2 m-3">
            <div className="flex flex-col *:m-4 *:text-2xl">
              <div>
                <strong>{`${t('Schedulability')}: `}</strong>
                {t(
                  SCHEDULABILITIES.find((schedulability) => schedulability.value === selectedEventPost.schedulability)
                    ?.label || '-'
                )}
              </div>
              <div>
                <strong>{`${t('Consequence of time adjustment')}: `}</strong> {selectedEventPost.schedulability_effect}
              </div>
            </div>

            <div className="flex flex-col *:m-4 *:text-2xl">
              <div>
                <strong>{`${t('Created at')}: `}</strong>
                {selectedEventPost.created_at ? Datetime.toLocalTime(selectedEventPost.created_at, 'longDay') : '-'}
              </div>
              <div>
                <strong>{`${t('Updated at')}: `}</strong>
                {selectedEventPost.updated_at ? Datetime.toLocalTime(selectedEventPost.updated_at, 'longDay') : '-'}
              </div>
              <div>
                <strong>{`${t('Created by')}: `}</strong>
                {selectedEventPost.created_by ? selectedEventPost.created_by : t('Unknown')}
              </div>
              <div>
                <strong>{`${t('Updated by')}: `}</strong>
                {selectedEventPost.updated_by ? selectedEventPost.updated_by : t('Unknown')}
              </div>
              <div>
                <strong>{`${t('Status')}: `}</strong>
                {/* e2e: event-status-text is the view-modal status selector — do not remove */}
                <span data-testid="event-status-text">{getLabelForEventStatus(selectedEventPost.status)}</span>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderInputField = (): ReactElement | null => {
    const htmlContent = getEventDescriptionHtml(selectedEventPost)
    if (!htmlContent) {
      return null
    }

    return (
      <div key="Input field" className="m-2 *:text-2xl">
        <div
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }}
          className={styles.EventPostViewModal_QuillEditor}
        />
      </div>
    )
  }

  const renderSettingInstructions = (linked_objects: LinkedObjectSettingInstructions[]): ReactElement => {
    const leftPanel: ReactElement[] = []
    const rightPanel: ReactElement[] = []

    linked_objects?.forEach((linkedObject, index) => {
      const key = `${linkedObject.level}-${linkedObject.name}-${linkedObject.attribute}-${index}`
      const settings = [
        ...(linkedObject.setting_instructions ?? []),
        ...(linkedObject.time_series_setting_instructions ?? []),
      ]

      const prettyPriorityName = (priority: number): string => {
        let prettyName = ''
        if (priority === 20) {
          prettyName = t('Planned')
        } else if (priority === 30) {
          prettyName = t('Unplanned')
        }
        return prettyName
      }

      const linkedObjectItem = (
        <div key={key}>
          <div className="flex flex-col *:m-4 *:text-2xl">
            <h3 className="italic">
              {getPrettyName(linkedObject.level, digitalTwin?.translations) +
                ' - ' +
                getPrettyName(linkedObject.name, digitalTwin?.translations) +
                (linkedObject.attribute ? ' - ' : '') +
                getPrettyName(linkedObject.attribute ?? '', digitalTwin?.translations)}
            </h3>
            {settings?.length ? (
              <p>{` ${settings?.length} ${settings?.length > 1 ? t('Settings') : t('Setting')}`}</p>
            ) : (
              <p>{t('Affected component. No added settings')}</p>
            )}
          </div>
          {settings?.map((setting, index) => {
            const property = allProperties.find(
              (property) =>
                property.name === setting.name &&
                property.attribute === setting.attribute &&
                property.level === setting.level
            )

            return (
              <>
                <div key={`${setting.level}_${index}`} className="m-1 *:m-4 *:text-2xl wrap-break-word">
                  {isTimeSeriesSettingInstruction(setting) && (
                    <div>
                      <strong>{t('Time series setting')}</strong>
                    </div>
                  )}
                  <div>
                    <strong>{`${t('Start time')}: `}</strong>
                    {setting.start_time ? Datetime.toLocalTime(setting.start_time as ISODateTime, 'longDay') : '-'}
                  </div>
                  <div>
                    <strong>{`${t('End time')}: `}</strong>
                    {setting.end_time
                      ? Datetime.toLocalTime(
                          Datetime.ISODatetimeTo59Minutes(setting.end_time as ISODateTime),
                          'longDay'
                        )
                      : '-'}
                  </div>
                  <div>
                    <strong>{`${t('Deviation type')}: `}</strong>
                    {prettyPriorityName(setting.priority)}
                  </div>
                  <div>
                    <strong>{`${t('Cause')}: `}</strong> {setting.comment}
                  </div>
                  <div>
                    <strong>{`${t('Value')}: `}</strong> {getSettingValueFromInstruction(setting)}
                    {property?.measurement_unit}
                  </div>
                </div>
                {settings.length > 1 && index !== settings.length - 1 && <Divider />}
              </>
            )
          })}
        </div>
      )

      if (index % 2 === 0) {
        leftPanel.push(linkedObjectItem)
      } else {
        rightPanel.push(linkedObjectItem)
      }
    })

    return (
      <>
        <div className="flex justify-between">
          <div className="w-1/2">{leftPanel}</div>
          <div className="w-1/2">{rightPanel}</div>
        </div>
      </>
    )
  }

  return (
    <>
      <Dialog
        open={true}
        fullWidth
        maxWidth="lg"
        onClose={(_, reason) => {
          if (reason === 'escapeKeyDown') {
            handleOnClose()
          }
        }}
        data-testid="event-view-dialog" // e2e: event view dialog selector — do not remove
      >
        <div data-printable className="p-0">
          <CopyDialogTitle
            title={selectedEventPost.name ?? ''}
            handleClose={() => handleOnClose()}
            onTitleClick={
              selectedEventPost.id
                ? () => {
                    const url = `${window.location.origin}/events/${selectedEventPost.id}`
                    navigator.clipboard.writeText(url)
                    alertStore.success(t('URL copied to clipboard'))
                  }
                : undefined
            }
            titleTooltip={selectedEventPost.id ? t('Click to copy link') : undefined}
            fontSize={48}
          />
          <DialogContent className="overflow-x-hidden">
            <div>
              <div className="flex flex-rows my-8 m-2 gap-6 *:text-3xl">
                <div>
                  <strong>{t('Start time')}:</strong> {Datetime.toLocalTime(selectedEventPost.start_time, 'longDay')}
                </div>
                <div>
                  <strong>{t('End time')}:</strong> {Datetime.toLocalTime(selectedEventPost.end_time, 'longDay')}
                </div>
              </div>
              <h2 className="m-1 text-2xl font-bold">{t('Information')}:</h2>
              {renderInformation()}
            </div>

            <div className="mt-5 overflow-x-hidden break-words">
              {/* Use Tailwind's `break-words` utility to ensure long description text wraps instead of overflowing */}
              <h2 className="m-1 text-2xl font-bold">{t('Description')}:</h2>
              {renderInputField()}
            </div>

            <div className="mt-10">
              {/* e2e: affected-components-header marks the event-info dialog — do not remove */}
              <h2 className="m-1 text-2xl font-bold" data-testid="affected-components-header">
                {t('Affected components & settings')}:
              </h2>
              {renderSettingInstructions(selectedEventPost.setting_instructions?.linked_objects ?? [])}
            </div>

            <div className="flex justify-end items-center *:m-1 *:flex *:items-center print:hidden print-hidden">
              <Button
                primary
                icon="fal fa-edit"
                disabled={isSandboxProject}
                disabledTooltip={t('Not available in sandbox project')}
                onClick={() => {
                  location.pathname.includes('/events') ? setOpenEventPostModal(true) : handleEditClick()
                }}
              >
                {t('Edit')}
              </Button>
              <div>
                <PrintButton
                  fileName={selectedEventPost.name}
                  title={t('Print')}
                  fitOverflowY={{ overflowInParentElement: true }}
                  hideSelectors={['.print-hidden']}
                />
              </div>
            </div>
          </DialogContent>
        </div>
      </Dialog>
      {openEventPostModal && (
        <EventPostModal selectedEventPostId={selectedEventPostId} closeModal={() => setOpenEventPostModal(false)} />
      )}
    </>
  )
}
